import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import type React from 'react';
import { useState } from 'react';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import { isRangeTooWide, MAX_RANGE_DAYS, maxRangeMessage } from 'utils/validateDateRange';

import type { DateRangeFilterValue } from '../smartFilterTypes';

interface DateRangeControlProps {
  value: DateRangeFilterValue | null;
  onChange: (value: DateRangeFilterValue) => void;
  /** Maximum number of days allowed between start and end (inclusive). Defaults to MAX_RANGE_DAYS (90). */
  maxRangeDays?: number;
}

const DATE_SX = {
  width: { xs: '100%', sm: 160 },
  minWidth: 0,
  flex: { xs: 1, sm: 'none' },
  '& .MuiOutlinedInput-root': {
    height: 36,
    borderRadius: RADIUS_ITEM,
    backgroundColor: SURFACE_CONTAINER_LOWEST,
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.85rem',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Jost, sans-serif',
    transform: 'translate(14px, 7px)',
    '&.Mui-focused, &.MuiFormLabel-filled': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
} as const;

const DateRangeControl: React.FC<DateRangeControlProps> = ({ value, onChange, maxRangeDays = MAX_RANGE_DAYS }) => {
  const current = value ?? { start: null, end: null };
  const startMoment = current.start ? moment(current.start) : null;
  const endMoment = current.end ? moment(current.end) : null;
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  const fireBlockFeedback = (msg: string) => setSnackbarMsg(msg);

  const tooWide = isRangeTooWide(startMoment, endMoment, maxRangeDays);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: '100%' }}
        >
          <DatePicker
            label="Desde"
            value={startMoment}
            onChange={(v) => {
              if (!v) {
                onChange({ ...current, start: null });
                return;
              }
              if (endMoment && isRangeTooWide(v, endMoment, maxRangeDays)) {
                // Hard block: surface snackbar instead of silently dropping end.
                fireBlockFeedback(maxRangeMessage(maxRangeDays));
                return;
              }
              // Snap to start-of-day so the opening date is inclusive from 00:00:00.
              onChange({ ...current, start: v.clone().startOf('day').toISOString() });
            }}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: DATE_SX } }}
          />
          <Typography
            sx={{
              color: `${NAVY}60`,
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.8rem',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            —
          </Typography>
          <DatePicker
            label="Hasta"
            value={endMoment}
            onChange={(v) => {
              if (!v) {
                onChange({ ...current, end: null });
                return;
              }
              if (startMoment && isRangeTooWide(startMoment, v, maxRangeDays)) {
                fireBlockFeedback(maxRangeMessage(maxRangeDays));
                return;
              }
              // Snap to end-of-day (23:59:59.999) so the backend's strict `lessThan`
              // comparison still includes rows created on the closing date.
              onChange({ ...current, end: v.clone().endOf('day').toISOString() });
            }}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: DATE_SX } }}
          />
        </Box>
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.7rem',
            color: tooWide ? '#c62828' : `${NAVY}70`,
            pl: 0.5,
          }}
        >
          {tooWide ? maxRangeMessage(maxRangeDays) : `Máximo ${maxRangeDays} días por rango`}
        </Typography>
      </Box>

      <Snackbar
        open={snackbarMsg !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbarMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSnackbarMsg(null)}
          sx={{ fontFamily: 'Jost, sans-serif' }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default DateRangeControl;
